/**
 * @fileoverview Constants for the PyMOL (WebAssembly) visualizer backend: the payload pin,
 * the MolStyles -> PyMOL representation table, and color tables.
 *
 * No imports with side effects: this file is loaded by mocha and during Next.js SSR.
 * See NOTES.md for the reasoning behind the tables.
 */

import {
    MolStyles, repsOfStyle, repSetOptions, transparencyByRep,
} from '../style_manager';

// ---------------------------------------------------------------------------------------------
// Payload contract (conifer-workspace INTERFACES.md section 9, pymol-web docs/phase1/payload.md)
// ---------------------------------------------------------------------------------------------

/** The one payload version this build of BMaps was written and tested against. */
export const PYMOL_PAYLOAD_VERSION = '0.1.3';
/** Wrapper API this backend requires: MAJOR.MINOR while MAJOR is 0, else MAJOR only. */
export const PYMOL_WRAPPER_API = '0.1';
/** Directory under the application's base path that holds `<version>/` payload directories. */
export const PYMOL_PAYLOAD_DIR = 'pymol';
/** Manifest schema the backend understands. */
export const PYMOL_PAYLOAD_SCHEMA = 1;

// ---------------------------------------------------------------------------------------------
// Names inside PyMOL
// ---------------------------------------------------------------------------------------------

/**
 * Reserved prefix of every BMaps-managed PyMOL object. A name must never be a selection
 * keyword (an object called `b` is silently renamed by PyMOL), and names with a leading
 * underscore are hidden from PyMOL's own listings.
 */
export const ObjectPrefix = '_bm_';
/** Scratch selection used (and deleted again) around selection-based commands. */
export const ScratchSelection = '_bm_sel';
/** Second scratch selection, used to accumulate a multi-object selection (zoom). */
export const ScratchSelection2 = '_bm_sel2';

/**
 * Partitioning policy for polymer atoms: one PyMOL object per (case, model, chain) when true,
 * one per (case, model) when false. See NOTES.md "Object partitioning".
 */
export const PROTEIN_OBJECT_PER_CHAIN = true;

/** Object kinds; also used in the object names (`_bm_pro3`), which helps when debugging. */
export const ObjectKinds = {
    protein: 'pro',
    compound: 'cmp',
    water: 'wat',
    fragment: 'frag',
    hotspot: 'hot',
    hetero: 'het',
    misc: 'misc',
    // milestone M2: not atoms of the BMaps model (never picked or hovered as atoms)
    surface: 'srf',
    shapes: 'shp',
    temp: 'tmp',
};

// ---------------------------------------------------------------------------------------------
// Representations
// ---------------------------------------------------------------------------------------------

/**
 * Bit order of the `bridge.apply_reps` masks (bit i = RepOrder[i]). A copy of
 * `PyMolViewer.REPS` of wrapper API 0.1 so masks can be computed without the payload; the
 * scene replaces it with the loaded wrapper's own list when that differs.
 */
export const RepOrder = [
    'lines', 'spheres', 'mesh', 'ribbon', 'cartoon', 'sticks', 'dots', 'surface', 'labels',
    'nonbonded', 'nb_spheres', 'cell', 'cgo', 'callback', 'extent', 'slice', 'dashes', 'angles',
    'dihedrals', 'ellipsoids', 'volume',
];

/**
 * The representations BMaps styles own. A restyle hides exactly these (minus the ones it
 * shows), so representations a user switched on through the native command line (mesh, dots,
 * surface, labels ...) are left alone.
 */
export const ManagedReps = ['lines', 'spheres', 'ribbon', 'cartoon', 'sticks', 'nonbonded', 'nb_spheres'];

/** @param {string[]} names @param {string[]} [order] @returns {number} bit mask */
export function repMask(names, order=RepOrder) {
    let mask = 0;
    for (const name of names) {
        const bit = order.indexOf(name);
        if (bit < 0) throw new Error(`PyMOL backend: unknown representation "${name}"`);
        mask |= (1 << bit); // eslint-disable-line no-bitwise
    }
    return mask;
}

/**
 * PyMOL's own defaults for the settings the style table uses, all applied to a batch of atom
 * ids with `bridge.set_atom_setting` (= `cmd.set(name, value, selection)`).
 * `stick_transparency` is a BOND-level setting: given a selection, PyMOL applies it to the
 * bonds between the selected atoms. Checked in a browser on payload 0.1.0 - sticks get dimmer
 * at the same footprint and come back with 0 - see NOTES.md "Verified in a browser".
 */
export const SettingDefaults = {
    sphere_scale: 1,
    sphere_transparency: 0,
    cartoon_transparency: 0,
    stick_transparency: 0,
    transparency: 0, // surfaces (rep sets)
};
/** Per-atom cartoon type (`cmd.cartoon`), PyMOL's default. */
export const DefaultCartoonType = 'automatic';

/**
 * Ribbon proportions per `CartoonStyle` preference (VisualizerInterface.SetCartoonStyle).
 *
 * `pymol` is PyMOL's own defaults (third_party/pymol-open-source layer1/SettingInfo.h), listed
 * so switching back restores them. `maestro` is what Schrödinger's own Maestro->PyMOL export
 * sets (schrodinger.utils.pymol.send_maestro_settings, 2022-3): its Maestro preference defaults
 * are ribbon width 1.61 Å and thickness 0.15 Å, thin-tube width 0.25 Å, halved where PyMOL
 * takes a half-length or radius, and helix interiors gray50 ("Helix interior: Gray"). Maestro's
 * Cartoon style - loops as thin tubes, sheets as arrows, helices as ribbons - is what PyMOL's
 * `automatic` cartoon already draws; only the proportions and the interior colour differ.
 * Maestro's sticks are 0.16 Å against PyMOL's 0.25 (`stick_radius`), so the profile carries
 * that too (added 2026-09-26). Styles that set a per-atom `stick_radius` keep it.
 * @type {Object<string, Object<string, number|string>>}
 */
export const CartoonProfiles = {
    pymol: {
        cartoon_oval_length: 1.35,
        cartoon_oval_width: 0.25,
        cartoon_rect_length: 1.40,
        cartoon_rect_width: 0.4,
        cartoon_loop_radius: 0.2,
        cartoon_highlight_color: 'default',
        stick_radius: 0.25,
    },
    maestro: {
        cartoon_oval_length: 0.805,
        cartoon_oval_width: 0.15,
        cartoon_rect_length: 0.805,
        cartoon_rect_width: 0.15,
        cartoon_loop_radius: 0.125,
        cartoon_highlight_color: 'gray50',
        stick_radius: 0.16,
    },
};
export const DefaultCartoonProfile = 'pymol';

/**
 * Ray-tracer settings per `CartoonStyle` profile, applied by PyMOLScene.rayTrace for the render
 * only (the live view is untouched; PyMOL computes ambient occlusion per vertex on the CPU, too
 * slow for a big surface in the browser). `maestro` is Maestro's "enhanced depth cues" - ambient
 * occlusion - and its angle-dependent transparency (surfaces go opaque at grazing angles). The
 * outline ("ray_trace_mode") stays the export dialog's own checkbox. `pymol` lists PyMOL's
 * defaults (layer1/SettingInfo.h) so the two profiles name the same settings.
 * @type {Object<string, Object<string, number>>}
 */
export const RenderProfiles = {
    pymol: {
        ambient_occlusion_mode: 0,
        ambient_occlusion_scale: 25,
        ambient_occlusion_smooth: 10,
        ray_transparency_oblique: 0,
        ray_transparency_oblique_power: 4,
    },
    maestro: {
        ambient_occlusion_mode: 1,
        ambient_occlusion_scale: 25,
        ambient_occlusion_smooth: 10,
        ray_transparency_oblique: 0.8,
        ray_transparency_oblique_power: 2,
    },
};

/**
 * MolStyles code -> what PyMOL shows.
 *   reps          representations shown (every other ManagedReps entry is hidden)
 *   visible       what atomIsVisible() answers. Mirrors Visualizer3Dmol, where `invisible`
 *                 (a clicksphere) counts as visible and `hidden` / `surface` do not.
 *   cartoonLike   colored per residue (selected > override > chain color), see NOTES.md
 *   settings      per-atom / per-bond settings this style needs (others are left as they are)
 *   cartoonType   per-atom cartoon type for cartoon-like styles
 *
 * `nb_spheres` / `nonbonded` only draw atoms without bonds (ions, lone waters): 3Dmol draws a
 * bondless atom in stick style as a small sphere, PyMOL's `sticks` would draw nothing.
 * @type {Object<string, {
 *   reps: string[], visible: boolean, cartoonLike?: boolean,
 *   settings?: Object<string, number>, cartoonType?: string,
 * }>}
 */
export const StyleTable = {
    [MolStyles.hidden]: { reps: [], visible: false },
    // Milestone M2 (DrawSurface). The atoms themselves show nothing, as in 3Dmol.
    [MolStyles.surface]: { reps: [], visible: false },
    [MolStyles.wireframe]: { reps: ['lines', 'nonbonded'], visible: true },
    [MolStyles.sticks]: {
        reps: ['sticks', 'nb_spheres'],
        visible: true,
        settings: { stick_transparency: 0 },
    },
    // 3Dmol: { stick: { opacity: 0.8 } }
    [MolStyles.ghost]: {
        reps: ['sticks', 'nb_spheres'],
        visible: true,
        settings: { stick_transparency: 0.2 },
    },
    // 3Dmol: { sphere: { scale: 0.3 }, stick: {} }
    [MolStyles.ballandstick]: {
        reps: ['sticks', 'spheres'],
        visible: true,
        settings: { sphere_scale: 0.3, sphere_transparency: 0, stick_transparency: 0 },
    },
    [MolStyles.spacefill]: {
        reps: ['spheres'],
        visible: true,
        settings: { sphere_scale: 1, sphere_transparency: 0 },
    },
    // Pickable but unrendered (3Dmol's clicksphere): a fully transparent sphere, which PyMOL
    // still picks because the scene sets transparency_picking_mode=1 (validated on our port,
    // pymol-web docs/phase1/wrapper.md section 8: 8/8 picks, lit fraction = background).
    [MolStyles.invisible]: {
        reps: ['spheres'],
        visible: true,
        settings: { sphere_scale: 1, sphere_transparency: 1 },
    },
    [MolStyles.cartoon]: {
        reps: ['cartoon'],
        visible: true,
        cartoonLike: true,
        cartoonType: 'automatic',
        settings: { cartoon_transparency: 0 },
    },
    // 3Dmol: cartoon with opacity 0.8
    [MolStyles.tribbons]: {
        reps: ['cartoon'],
        visible: true,
        cartoonLike: true,
        cartoonType: 'automatic',
        settings: { cartoon_transparency: 0.2 },
    },
    // 3Dmol: { cartoon: { trace: true } } - a tube through the guide atoms
    [MolStyles.backbone]: {
        reps: ['cartoon'],
        visible: true,
        cartoonLike: true,
        cartoonType: 'tube',
        settings: { cartoon_transparency: 0 },
    },
    // 3Dmol: { cartoon: {} } - its plain ribbon. PyMOL's `ribbon` rep is the closest match.
    [MolStyles.ribbons]: { reps: ['ribbon'], visible: true, cartoonLike: true },
};
// 'default' and the 2D-panel style resolve like Visualizer3Dmol.makeStyleSpec() does.
StyleTable[MolStyles.default] = StyleTable[MolStyles.sticks];
StyleTable['2dview'] = StyleTable[MolStyles.sticks];

/**
 * How long the pointer has to rest on an atom before 'mouseenter' is published (ms).
 * 3Dmol's default `hoverDuration`, which BMaps never changes.
 */
export const HoverDwellMs = 500;

/**
 * Largest side of a ray-traced export (pixels). Ray-tracing time and memory grow with the pixel
 * count and the render blocks the page: 4096 keeps a 4x export of a typical view in bounds.
 */
export const RayMaxDimension = 4096;

/** Style of an atom that was added but not styled yet (3Dmol's default atom style is lines). */
export const InitialStyle = MolStyles.wireframe;

/**
 * Per-state transparency setting of each representation (temporary molecules: one state per
 * molecule, see PyMOLTempMolecules.js). `lines` has none in PyMOL.
 */
export const RepTransparencySetting = {
    sticks: 'stick_transparency',
    spheres: 'sphere_transparency',
    nb_spheres: 'nonbonded_transparency',
    nonbonded: 'nonbonded_transparency',
    cartoon: 'cartoon_transparency',
    ribbon: 'ribbon_transparency',
};

// ---------------------------------------------------------------------------------------------
// Milestone M2: surfaces, shapes, labels (3Dmol's defaults, which BMaps relies on)
// ---------------------------------------------------------------------------------------------

/** Visualizer3Dmol.DrawSurface's default opacity. */
export const DefaultSurfaceOpacity = 0.8;
/**
 * Probe radius of BMaps surfaces: PyMOL's own default, set per object so a global change typed
 * on the command line does not reshape them. 3Dmol's "VDW" surface (a smoothed grid surface)
 * looks like PyMOL's solvent-excluded surface with this probe; smaller probes looked lumpier
 * and, on the dense overlapping poses of a hotspot, lost most of the surface (VERIFIED: probe
 * 0.5 at surface_quality 0 kept a faceted fraction of the cloud). surface_quality stays 0:
 * quality 1 cost 8x the time (166 vs 20 ms for a 128-atom cloud). See NOTES.md "Surfaces".
 */
export const SurfaceSolventRadius = 1.4;

/** 3Dmol GLShape defaults for the shapes BMaps draws. */
export const ShapeDefaults = {
    sphereRadius: 1.5,
    cylinderRadius: 0.1,
    arrowRadius: 0.1,
    /** Visualizer3Dmol.drawArrow(..., radiusRatio=1.0): cone radius = radius * radiusRatio. */
    arrowRadiusRatio: 1.0,
    /** GLShape.addArrow: the shaft ends (and the cone starts) at this fraction of the length. */
    arrowMid: 0.618034,
    /** GLShape.addDashedCylinder */
    dashLength: 0.25,
    gapLength: 0.25,
    /** drawLine (no BMaps caller): a thin rod instead of a 1-pixel GL line. */
    lineRadius: 0.04,
};

/** 3Dmol Label defaults (Label.ts setContext). */
export const LabelDefaults = {
    font: 'sans-serif',
    fontSize: 18,
    fontColor: 0xffffff,
    backgroundColor: 0x000000,
    padding: 4,
    borderRadius: 6,
    /** text line height factor 3Dmol allows for descenders */
    lineHeight: 1.25,
    alignment: 'topLeft',
};

/** @returns {object|null} the StyleTable entry, or null for an unknown style code */
export function getStyleSpec(style) {
    if (Object.prototype.hasOwnProperty.call(StyleTable, style)) return StyleTable[style];
    const reps = repsOfStyle(style);
    return reps ? repSetSpec(style, reps) : null;
}

/**
 * PyMOL representations of each rep-set name (B3 option B, style_manager RepNames). Lines and
 * sticks bring the bondless-atom reps along, as the wireframe / sticks styles do.
 */
export const RepSetReps = {
    lines: ['lines', 'nonbonded'],
    sticks: ['sticks', 'nb_spheres'],
    spheres: ['spheres'],
    cartoon: ['cartoon'],
    ribbon: ['ribbon'],
    surface: ['surface'],
    mesh: ['mesh'],
    dots: ['dots'],
};

/**
 * Everything a rep set can show. An atom with a rep set - or leaving one - has all of these
 * managed (hidden unless shown), not just ManagedReps: its surface / mesh / dots are BMaps'.
 */
export const RepSetManagedReps = [...new Set(Object.values(RepSetReps).flat())];

const repSetSpecs = new Map();

/**
 * The StyleTable-like spec of a rep set, cached per style value. `cartoonLike` (colored per
 * residue, like the cartoon style) only when the set draws nothing but cartoon / ribbon; with
 * atom reps too, `cartoonColored`: the atoms keep their own colors and the cartoon / ribbon get the
 * per-residue color through the cartoon_color / ribbon_color atom settings.
 *
 * The set's transparency goes to sticks, spheres, cartoon and surface - the reps whose
 * transparency PyMOL keeps per atom (per bond for sticks). Lines, ribbon (an object-state
 * setting), mesh and dots stay opaque; 3Dmol draws the same reps transparent.
 */
function repSetSpec(style, reps) {
    if (repSetSpecs.has(style)) return repSetSpecs.get(style);
    const has = (name) => reps.includes(name);
    const { transparency, cartoonType } = repSetOptions(style);
    const t = transparencyByRep(transparency); // per rep
    const ribbonish = has('cartoon') || has('ribbon');
    const atomReps = has('lines') || has('sticks') || has('spheres');
    const settings = {};
    if (has('sticks')) settings.stick_transparency = t.sticks;
    if (has('spheres')) Object.assign(settings, { sphere_scale: 1, sphere_transparency: t.spheres });
    if (has('cartoon')) settings.cartoon_transparency = t.cartoon;
    if (has('surface')) settings.transparency = t.surface;
    const spec = {
        reps: reps.flatMap((name) => RepSetReps[name] || []),
        visible: ribbonish || atomReps,
        manages: RepSetManagedReps,
        settings,
        ...(has('cartoon') ? { cartoonType } : {}),
        ...(ribbonish && !atomReps ? { cartoonLike: true } : {}),
        ...(ribbonish && atomReps ? { cartoonColored: true } : {}),
    };
    repSetSpecs.set(style, spec);
    return spec;
}

// ---------------------------------------------------------------------------------------------
// Colors
// ---------------------------------------------------------------------------------------------

/**
 * Element colors used when an atom has no `defaultColorMap` entry: 3Dmol's "rasmol" table,
 * which is what Visualizer3Dmol falls back to, so both backends color a protein alike.
 */
export const ElementColors = {
    H: 0xffffff,
    He: 0xffc0cb,
    Li: 0xb22222,
    B: 0x00ff00,
    C: 0xc8c8c8,
    N: 0x8f8fff,
    O: 0xf00000,
    F: 0xdaa520,
    Na: 0x0000ff,
    Mg: 0x228b22,
    Al: 0x808090,
    Si: 0xdaa520,
    P: 0xffa500,
    S: 0xffc832,
    Cl: 0x00ff00,
    Ca: 0x808090,
    Ti: 0x808090,
    Cr: 0x808090,
    Mn: 0x808090,
    Fe: 0xffa500,
    Ni: 0xa52a2a,
    Cu: 0xa52a2a,
    Zn: 0xa52a2a,
    Br: 0xa52a2a,
    Ag: 0x808090,
    I: 0xa020f0,
    Ba: 0xffa500,
    Au: 0xdaa520,
};
/** 3Dmol's `elementColors.defaultColor` (deep pink): an element without a table entry. */
export const DefaultElementColor = 0xff1493;

/** @param {string} elem element symbol in any case @returns {number} 0xRRGGBB */
export function elementColor(elem) {
    if (!elem) return DefaultElementColor;
    const symbol = elem.charAt(0).toUpperCase() + elem.slice(1).toLowerCase();
    const color = ElementColors[symbol];
    return color == null ? DefaultElementColor : color;
}

/** The CSS color names BMaps code passes around (plus the basic CSS set). */
const NamedColors = {
    black: 0x000000,
    white: 0xffffff,
    red: 0xff0000,
    green: 0x008000,
    lime: 0x00ff00,
    blue: 0x0000ff,
    yellow: 0xffff00,
    cyan: 0x00ffff,
    aqua: 0x00ffff,
    magenta: 0xff00ff,
    fuchsia: 0xff00ff,
    orange: 0xffa500,
    purple: 0x800080,
    pink: 0xffc0cb,
    hotpink: 0xff69b4,
    gray: 0x808080,
    grey: 0x808080,
    silver: 0xc0c0c0,
    maroon: 0x800000,
    olive: 0x808000,
    navy: 0x000080,
    teal: 0x008080,
    brown: 0xa52a2a,
    gold: 0xffd700,
    salmon: 0xfa8072,
    violet: 0xee82ee,
    tan: 0xd2b48c,
};

/**
 * Convert the color forms that cross the visualizer seam to 0xRRGGBB.
 * @param {number|string|{hex: number}} color a number, '#rgb', '#rrggbb', '0xRRGGBB',
 *   'rgb(r, g, b)', a CSS color name, or a `{hex}` object (themes.BackgroundColors)
 * @returns {number|null} null when the value is not a color
 */
export function colorToInt(color) {
    if (color == null) return null;
    if (typeof color === 'number') {
        // eslint-disable-next-line no-bitwise
        return Number.isFinite(color) ? (Math.round(color) & 0xffffff) : null;
    }
    if (typeof color === 'object') return colorToInt(color.hex);
    if (typeof color !== 'string') return null;
    const text = color.trim().toLowerCase();
    let match = /^#([0-9a-f]{3})$/.exec(text);
    if (match) {
        const [r, g, b] = match[1].split('').map((c) => parseInt(c + c, 16));
        return (r * 65536) + (g * 256) + b;
    }
    match = /^(?:#|0x)([0-9a-f]{6})$/.exec(text);
    if (match) return parseInt(match[1], 16);
    match = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/.exec(text);
    if (match) {
        const [r, g, b] = match.slice(1, 4).map((n) => Math.min(255, parseInt(n, 10)));
        return (r * 65536) + (g * 256) + b;
    }
    const named = NamedColors[text];
    return named == null ? null : named;
}

/** @param {number} color 0xRRGGBB @returns {string} PyMOL's hex color spelling, '0xrrggbb' */
export function pymolColorName(color) {
    return `0x${color.toString(16).padStart(6, '0')}`;
}

/**
 * Image pixels per CSS pixel of a screen-frame export (Export -> Download Image, not ray-traced):
 * what 3Dmol's drawing buffer has, so the export is the same size with either viewer. BMaps' 3Dmol
 * fork (lib/js/3Dmol-bmaps.js, setSize) uses the display's devicePixelRatio and doubles it below 2
 * because it antialiases: 1 -> 2, 1.5 -> 3, 2 -> 2, 3 -> 3. Without this a DPR-1 screen exported
 * half 3Dmol's width and height (960 x 600 against 1920 x 1200 in the harness).
 * @param {number} dpr window.devicePixelRatio (1 when unknown)
 * @returns {number}
 */
export function screenCaptureScale(dpr) {
    const r = Number(dpr) > 0 ? Number(dpr) : 1;
    return r < 2 ? r * 2 : r;
}
